import { createEnum } from '../helpers'

export const NC_PROVINCIAL_CONTRIBUTIONS_IMPORT_ERRORS = [
  'zodSchemaError',
  'invalidProvince',
] as const

export const NC_PROVINCIAL_CONTRIBUTIONS_IMPORT_ERROR_TYPES = createEnum(
  NC_PROVINCIAL_CONTRIBUTIONS_IMPORT_ERRORS,
)
export type NcProvincialContributionsImportErrorType =
  (typeof NC_PROVINCIAL_CONTRIBUTIONS_IMPORT_ERRORS)[number]

export const NC_PROVINCIAL_CONTRIBUTIONS_CREATE_ERRORS = ['crtPeriodConflict'] as const

export const NC_PROVINCIAL_CONTRIBUTIONS_CREATE_ERROR_TYPES = createEnum(
  NC_PROVINCIAL_CONTRIBUTIONS_CREATE_ERRORS,
)
export type NcProvincialContributionsCreateErrorType =
  (typeof NC_PROVINCIAL_CONTRIBUTIONS_CREATE_ERRORS)[number]

export const NC_PROVINCIAL_CONTRIBUTIONS_CREATE_CONSTRAINT_TO_ERROR: Record<
  string,
  NcProvincialContributionsCreateErrorType
> = {
  volumes_nc_provincial_contributions_crt_period_unique:
    NC_PROVINCIAL_CONTRIBUTIONS_CREATE_ERROR_TYPES.crtPeriodConflict,
}

export const DISTRIBUTION_CHANNELS = ['Domestic', 'Duty Free'] as const
export type DistributionChannel = (typeof DISTRIBUTION_CHANNELS)[number]

export const CHANNEL_CONTRIBUTIONS_IMPORT_ERRORS = ['zodSchemaError', 'invalidSubChannel'] as const

export const CHANNEL_CONTRIBUTIONS_IMPORT_ERROR_TYPES = createEnum(
  CHANNEL_CONTRIBUTIONS_IMPORT_ERRORS,
)
export type ChannelContributionsImportErrorType =
  (typeof CHANNEL_CONTRIBUTIONS_IMPORT_ERRORS)[number]

export const CHANNEL_CONTRIBUTIONS_CREATE_ERRORS = ['crtPeriodConflict'] as const

export const CHANNEL_CONTRIBUTIONS_CREATE_ERROR_TYPES = createEnum(
  CHANNEL_CONTRIBUTIONS_CREATE_ERRORS,
)
export type ChannelContributionsCreateErrorType =
  (typeof CHANNEL_CONTRIBUTIONS_CREATE_ERRORS)[number]

export const CHANNEL_CONTRIBUTIONS_CREATE_CONSTRAINT_TO_ERROR: Record<
  string,
  ChannelContributionsCreateErrorType
> = {
  volumes_channel_contributions_crt_period_unique:
    CHANNEL_CONTRIBUTIONS_CREATE_ERROR_TYPES.crtPeriodConflict,
}

const VOLUME_FORECAST_STATUS_INVALID = [
  'INVALID_MATERIAL_NOT_FOUND',
  'INVALID_MATERIAL_NOT_COMPLETE',
  'INVALID_NC_PROVINCIAL_VOLUMES_NOT_FOUND',
  'INVALID_CHANNEL_VOLUMES_NOT_FOUND',
  'INVALID_NC_PROVINCIAL_AND_CHANNEL_VOLUMES_NOT_EQUAL',
] as const
const VOLUME_FORECAST_STATUS_WARNING = [] as const
export const VOLUME_FORECAST_STATUS_VALUES = [
  ...VOLUME_FORECAST_STATUS_INVALID,
  ...VOLUME_FORECAST_STATUS_WARNING,
] as const

export const VOLUME_FORECAST_STATUSES = createEnum(VOLUME_FORECAST_STATUS_VALUES)
export type VolumeForecastStatus = (typeof VOLUME_FORECAST_STATUS_VALUES)[number]

export function isVolumeForecastStatusInvalid(status: VolumeForecastStatus): boolean {
  return (VOLUME_FORECAST_STATUS_INVALID as unknown as VolumeForecastStatus[]).includes(status)
}

/** Severity order, most severe first. Used for prioritized sorting on the status column. */
export const VOLUME_FORECAST_STATUS_PRIORITY: readonly VolumeForecastStatus[] = [
  VOLUME_FORECAST_STATUSES.INVALID_MATERIAL_NOT_FOUND,
  VOLUME_FORECAST_STATUSES.INVALID_MATERIAL_NOT_COMPLETE,
  VOLUME_FORECAST_STATUSES.INVALID_NC_PROVINCIAL_VOLUMES_NOT_FOUND,
  VOLUME_FORECAST_STATUSES.INVALID_CHANNEL_VOLUMES_NOT_FOUND,
  VOLUME_FORECAST_STATUSES.INVALID_NC_PROVINCIAL_AND_CHANNEL_VOLUMES_NOT_EQUAL,
] as const

/**
 * Lifecycle of the cached corrected volume forecasts for one CRT period
 * (`fuze_crt.vol_corrected_forecasts_status`). `pending` also covers the case where the worker ran
 * but one of the two source imports was missing, so nothing could be computed yet.
 */
export const VOL_CORRECTED_FORECAST_STATUSES = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  FAILED: 'failed',
} as const

export type VolCorrectedForecastStatus =
  (typeof VOL_CORRECTED_FORECAST_STATUSES)[keyof typeof VOL_CORRECTED_FORECAST_STATUSES]

/**
 * Status filter values exposed in the volume forecasts table UI. 'VALID' matches
 * any row with no invalid statuses; each invalid status matches rows carrying it.
 */
export const VOLUME_FORECAST_STATUS_FILTER_VALID = 'VALID' as const
export const VOLUME_FORECAST_STATUS_FILTER_VALUES = [
  VOLUME_FORECAST_STATUS_FILTER_VALID,
  ...VOLUME_FORECAST_STATUS_INVALID,
] as const
export type VolumeForecastStatusFilter = (typeof VOLUME_FORECAST_STATUS_FILTER_VALUES)[number]
